import React from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';

/**
 * Editorial product tile — links to the deep-linkable /products/:slug page.
 * Used on collection pages and home page featured grids.
 */
export const ProductTile = ({ product, eager = false }) => {
  const isSold = !!product.sold;
  const displayPrice =
    product.price !== null
      ? `$${product.price}`
      : product.priceLabel || 'Inquire';
  const isApparel = product.collection === 'apparel';
  const isBook = product.collection === 'books';

  return (
    <Link
      to={`/products/${product.slug}`}
      className="group block focus:outline-none"
      data-testid={`product-tile-${product.slug}`}
    >
      <div
        className={cn(
          'relative overflow-hidden aspect-[4/5]',
          isApparel || isBook ? 'bg-cream-deep' : 'bg-ink/[0.03]'
        )}
      >
        <img
          src={product.image}
          alt={product.title}
          loading={eager ? 'eager' : 'lazy'}
          className={cn(
            'w-full h-full transition-transform duration-[900ms] ease-out group-hover:scale-[1.04]',
            isApparel
              ? 'object-contain p-6 mix-blend-multiply'
              : isBook
              ? 'object-contain p-4'
              : 'object-cover',
            isSold && 'grayscale-[35%] opacity-90'
          )}
        />
        {isSold && (
          <>
            <div className="absolute inset-0 bg-ink/15 pointer-events-none" />
            <span
              className="absolute top-4 right-4 px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] font-medium bg-ink text-cream"
              data-testid="tile-sold-badge"
            >
              Sold
            </span>
          </>
        )}
        {product.badge && !isSold && (
          <span
            className={cn(
              'absolute top-4 left-4 px-3 py-1.5',
              'text-[10px] uppercase tracking-[0.18em] font-medium',
              'bg-cream/95 text-ink backdrop-blur-sm'
            )}
          >
            {product.badge}
          </span>
        )}
      </div>
      <div className="pt-5 flex items-baseline justify-between gap-4">
        <h3 className={cn(
          "font-display text-xl leading-snug group-hover:text-terracotta transition-colors",
          isSold ? "text-ink/60" : "text-ink"
        )}>
          {product.title}
        </h3>
        <span
          className={cn(
            'text-sm tabular-nums whitespace-nowrap',
            isSold
              ? 'text-ink/40 line-through'
              : product.price !== null
              ? 'text-ink'
              : 'text-terracotta italic'
          )}
        >
          {isSold ? 'Sold' : displayPrice}
        </span>
      </div>
    </Link>
  );
};

export default ProductTile;
